import { Control, FieldError, FieldValues, Path, useController } from "react-hook-form";
import { useEffect, useState } from "react";
import { FormHelperText, TextField } from "@mui/material";

interface InputFechaProps<K extends FieldValues> {
  label: string;
  name: Path<K>;
  control: Control<K>;
  error?: FieldError;
  fecha?: null | undefined | Date;
  esReadOnly?: boolean;
  rangoFechaInicio?: string;
  rangoFechaTermino?: string;
  onlyMonthPicker?: boolean;
  disabled?: boolean;
  
  onChange?: (fecha: Date | null) => void;
}

InputFecha.defaultProps = {
  esReadOnly: false,
  rangoFechaInicio: "1900-01-01",
  rangoFechaTermino: "2999-12-31",
  onlyMonthPicker: false,
  disabled: false,
};

export default function InputFecha<T extends FieldValues>(props: InputFechaProps<T>) {
  const { field: fechaField } = useController({
    name: props.name,
    control: props.control,
  });

  const [selectedDate, setSelectedDate] = useState<string>(""); // texto mostrado en el input
  const [isFocused, setIsFocused] = useState(false);

  const convertirJSONaLocalDate = (fecha: unknown): Date | null => {
    if (!fecha && fecha !== 0) return null;
    if (typeof fecha === "string") {
      const date = new Date(fecha);
      return isNaN(date.getTime()) ? null : date;
    }
    return fecha instanceof Date ? fecha : null;
  };

  function formatDate(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    if (props.onlyMonthPicker) {
      return `${year}-${month}`; // YYYY-MM
    }
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // YYYY-MM-DD
  }

  const formatFromFieldValue = (val: unknown) => {
    const d = convertirJSONaLocalDate(val);
    return d ? formatDate(d) : "";
  };

  // inicializa selectedDate a partir de props.fecha (si existe) o del valor del field
  useEffect(() => {
    const initial = props.fecha !== undefined && props.fecha !== null
      ? formatDate(convertirJSONaLocalDate(props.fecha)!)
      : formatFromFieldValue(fechaField.value);

    setSelectedDate(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // solo en mount

  // sincroniza cuando cambian props.fecha o el valor del field, pero SOLO cuando no estamos escribiendo (no focused)
  useEffect(() => {
    if (isFocused) return;
    const fieldStr = formatFromFieldValue(fechaField.value);
    if (fieldStr !== selectedDate) setSelectedDate(fieldStr);
  }, [fechaField.value, isFocused]);

  // convierte el valor del input (YYYY-MM-DD o YYYY-MM) a lo que se guarda en el field
  const convertToFieldValue = (val: string | null) => {
    if (!val) return null;
    if (props.onlyMonthPicker) {
      // val = "YYYY-MM" -> guardamos "YYYY-MM-01T00:00:00"
      return `${val}-01T00:00:00`;
    }
    // val = "YYYY-MM-DD" -> guardamos "YYYY-MM-DDT00:00:00"
    return `${val}T00:00:00`;
  };

  const expectedLength = props.onlyMonthPicker ? 7 : 10; // 7 = "YYYY-MM", 10 = "YYYY-MM-DD"

  return (
    <>
      <TextField
        label={props.label}
        type={props.onlyMonthPicker ? "month" : "date"}
        value={selectedDate ?? ""}
        variant="standard"
        fullWidth
        margin="dense"
        inputProps={{
          readOnly: props.esReadOnly,
          min: props.rangoFechaInicio,
          max: props.rangoFechaTermino,
        }}
        InputLabelProps={{ shrink: true }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          // al perder foco, validamos y guardamos en react-hook-form
          const finalVal = selectedDate && selectedDate.length === expectedLength ? convertToFieldValue(selectedDate) : null;
          fechaField.onChange(finalVal);
          if (props.onChange) {
            const dateObj = (finalVal && typeof finalVal === "string") ? new Date(finalVal) : null;
            props.onChange(dateObj);
          }
        }}
        onChange={(e) => {
          const val = e.target.value;
          setSelectedDate(val);

          // Solo actualizamos el field en RHF si el valor ya está completo (evita pisados durante tipeo)
          if (val && val.length === expectedLength) {
            const fechaFinal = convertToFieldValue(val);
            fechaField.onChange(fechaFinal);
            if (props.onChange) {
              const dateObj = new Date(fechaFinal!);
              if (!isNaN(dateObj.getTime())) props.onChange(dateObj);
            }
          }
        }}
        error={!!props.error}
        disabled={props.disabled}
      />
      {props.error && <FormHelperText error>{props.error.message}</FormHelperText>}
    </>
  );
}
